import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Fab,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CustomerHeader from './CustomerHeader';
import app from '../firebase';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import jsQR from 'jsqr';
import { QrCodeScanner } from '@mui/icons-material';

const CustomerLayout = ({ children }) => {
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [videoRef, setVideoRef] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Debug user state
  useEffect(() => {
    console.log('CustomerLayout - User state changed:', user);
  }, [user]);

  const auth = getAuth(app);

// ✅ Setup reCAPTCHA only when login dialog is opened
useEffect(() => {
  if (showLoginDialog) {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        getAuth(),
        'recaptcha-container',
        {
          size: 'invisible',
          callback: (response) => {
            console.log('reCAPTCHA solved:', response);
          },
        }
      );
    }
  }

  return () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear?.(); // optional
      window.recaptchaVerifier = null;
    }
  };
}, [showLoginDialog]);

// Handle video element when QR scanner opens
useEffect(() => {
  if (showQrScanner && videoRef && cameraStream) {
    console.log('Setting up video stream:', videoRef, cameraStream);
    videoRef.srcObject = cameraStream;
    videoRef.play().then(() => {
      console.log('Video started playing');
      scanQRCode();
    }).catch(error => {
      console.error('Error playing video:', error);
    });
  }
}, [showQrScanner, videoRef, cameraStream]);

// Cleanup camera stream on unmount
useEffect(() => {
  return () => {
    if (cameraStream) {
      const tracks = cameraStream.getTracks();
      tracks.forEach(track => track.stop());
    }
  };
}, [cameraStream]);




  const handleSendOtp = async () => {
    try {
      const appVerifier = window.recaptchaVerifier;
      const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
      const result = await signInWithPhoneNumber(auth, formattedPhone, appVerifier);
      setConfirmationResult(result);
      setIsOtpSent(true);
    } catch (error) {
      console.error('Failed to send OTP:', error);
      alert(error.message);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      if (!confirmationResult) return;
      const userCredential = await confirmationResult.confirm(otp);
      console.log('User logged in:', userCredential.user);
      console.log('User UID:', userCredential.user.uid);
      console.log('User phone:', userCredential.user.phoneNumber);
      setShowLoginDialog(false);
      setIsOtpSent(false);
      setPhone('');
      setOtp('');
      // Note: Removed automatic redirect to redemption page
      // Users can now choose where to go after login
    } catch (error) {
      console.error('Failed to verify OTP:', error);
      alert(error.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Note: Removed automatic redirect from home page to redemption page
  // Users can now access both product verification and redemption pages

  // Handle logout redirect
  useEffect(() => {
    if (!user && window.location.pathname === '/redeem') {
      navigate('/');
    }
  }, [user, navigate]);

  const startQRScanner = async () => {
    try {
      setShowQrScanner(true);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      setCameraStream(stream);
      setScanning(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please ensure camera permissions are granted.');
      setShowQrScanner(false);
      setScanning(false);
    }
  };

  const scanQRCode = () => {
    if (!videoRef || !scanning) return;

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (videoRef.readyState === videoRef.HAVE_ENOUGH_DATA) {
      canvas.height = videoRef.videoHeight;
      canvas.width = videoRef.videoWidth;
      context.drawImage(videoRef, 0, 0, canvas.width, canvas.height);
      
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      
      if (code) {
        handleQRCodeScanned(code.data);
        return;
      }
    }
    
    if (scanning) {
      requestAnimationFrame(scanQRCode);
    }
  };

  const handleQRCodeScanned = (qrData) => {
    setScanning(false);
    setShowQrScanner(false);
    
    // Stop video stream
    if (cameraStream) {
      const tracks = cameraStream.getTracks();
      tracks.forEach(track => track.stop());
      setCameraStream(null);
    }
    
    if (videoRef) {
      videoRef.srcObject = null;
    }
    
    // Check if QR code contains our verification URL
    if (qrData.includes('/verify?region=')) {
      // Extract region from URL
      const url = new URL(qrData);
      const region = url.searchParams.get('region');
      if (region) {
        navigate(`/verify?region=${region}`);
        // Call the QR scan handler if available
        setTimeout(() => {
          if (window.handleQRCodeScan) {
            window.handleQRCodeScan(qrData);
          }
        }, 100);
        return;
      }
    }
    
    // If it's just a region code, navigate to verification
    if (qrData.length <= 10) {
      navigate(`/verify?region=${qrData}`);
      // Call the QR scan handler if available
      setTimeout(() => {
        if (window.handleQRCodeScan) {
          window.handleQRCodeScan(qrData);
        }
      }, 100);
      return;
    }
    
    // If it's a URL, try to navigate to it
    if (qrData.startsWith('http')) {
      window.open(qrData, '_blank');
      return;
    }
    
    // Fallback: treat as region code
    navigate(`/verify?region=${qrData}`);
    // Call the QR scan handler if available
    setTimeout(() => {
      if (window.handleQRCodeScan) {
        window.handleQRCodeScan(qrData);
      }
    }, 100);
  };

  const stopQRScanner = () => {
    setScanning(false);
    setShowQrScanner(false);
    
    if (cameraStream) {
      const tracks = cameraStream.getTracks();
      tracks.forEach(track => track.stop());
      setCameraStream(null);
    }
    
    if (videoRef) {
      videoRef.srcObject = null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <CustomerHeader 
        onStartQRScanner={startQRScanner} 
        onShowLogin={() => setShowLoginDialog(true)}
      />

      {/* Login Dialog */}
      <Dialog open={showLoginDialog} onClose={() => setShowLoginDialog(false)}>
        <DialogTitle>Login with Phone Number</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Phone Number (e.g. +1234567890)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={isOtpSent}
              margin="normal"
            />
            {/* 🔹 reCAPTCHA container */}
            {isOtpSent && (
              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                margin="normal"
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLoginDialog(false)}>Cancel</Button>
          {!isOtpSent ? (
            <Button onClick={handleSendOtp} variant="contained">
              Send OTP
            </Button>
          ) : (
            <Button onClick={handleVerifyOtp} variant="contained">
              Verify OTP
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* QR Scanner Dialog */}
      <Dialog 
        open={showQrScanner} 
        onClose={stopQRScanner}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QrCodeScanner sx={{ mr: 1 }} />
            Scan QR Code
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Point your camera at the QR code to scan
            </Typography>
            <Box 
              sx={{ 
                position: 'relative',
                width: '100%',
                maxWidth: 400,
                mx: 'auto',
                mt: 2
              }}
            >
              <video
                ref={(el) => {
                  console.log('Video ref callback:', el);
                  setVideoRef(el);
                }}
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 8,
                  border: '2px solid #1976d2',
                  backgroundColor: '#f5f5f5'
                }}
                playsInline
                muted
                autoPlay
                onLoadedMetadata={() => {
                  console.log('Video metadata loaded');
                }}
                onCanPlay={() => {
                  console.log('Video can play');
                }}
                onError={(e) => {
                  console.error('Video error:', e);
                }}
              />
              {scanning && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 200,
                    height: 200,
                    border: '2px solid #4caf50',
                    borderRadius: 2,
                    pointerEvents: 'none'
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={stopQRScanner} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      {location.pathname === '/' ? (
        children
      ) : (
        <Container sx={{ mt: 4 }}>
          {children}
        </Container>
      )}

      {/* Floating QR Scanner Button - Only show on product verification page */}
      {location.pathname === '/verify' && (
        <Fab
          color="primary"
          aria-label="scan qr"
          onClick={startQRScanner}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <QrCodeScanner />
        </Fab>
      )}

      <div id="recaptcha-container" />
    </Box>
    
  );
};

export default CustomerLayout;
